import type { JudgeProfile } from "../types/database";
import { supabase } from "./supabase";

export async function getMyJudgeProfile(): Promise<JudgeProfile | null> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return null;
  const { data, error } = await supabase
    .from("judge_profiles")
    .select("*")
    .eq("user_id", userData.user.id)
    .maybeSingle();
  if (error || !data) return null;
  return data as JudgeProfile;
}

export type UpdateJudgeProfileInput = {
  headline?: string | null;
  organization?: string | null;
  bio?: string | null;
  linkedin_url?: string | null;
  areas_of_expertise?: string[];
  is_available?: boolean;
};

export async function upsertMyJudgeProfile(
  input: UpdateJudgeProfileInput
): Promise<{ error: Error | null }> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return { error: new Error("Not signed in") };
  const nowIso = new Date().toISOString();
  const { error } = await supabase.from("judge_profiles").upsert(
    {
      user_id: userData.user.id,
      headline: input.headline ?? null,
      organization: input.organization ?? null,
      bio: input.bio ?? null,
      linkedin_url: input.linkedin_url ?? null,
      areas_of_expertise: input.areas_of_expertise ?? [],
      is_available: input.is_available ?? true,
      updated_at: nowIso,
    },
    { onConflict: "user_id" }
  );
  return { error: error ? new Error(error.message) : null };
}

/** Toggle just the availability flag without disturbing the rest of the row. */
export async function setJudgeAvailability(
  available: boolean
): Promise<{ error: Error | null }> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return { error: new Error("Not signed in") };
  const userId = userData.user.id;

  // Try a plain update first — if no row exists, insert a default one.
  const { data: existing } = await supabase
    .from("judge_profiles")
    .select("user_id")
    .eq("user_id", userId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from("judge_profiles")
      .update({ is_available: available, updated_at: new Date().toISOString() })
      .eq("user_id", userId);
    return { error: error ? new Error(error.message) : null };
  }
  const { error } = await supabase.from("judge_profiles").insert({
    user_id: userId,
    is_available: available,
    areas_of_expertise: [],
  });
  return { error: error ? new Error(error.message) : null };
}
